/** @jsxImportSource @emotion/react */
import {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useEffect,
} from 'react'
import { PlayerState } from './types'
import { playerReducer, initialState } from './reducer'
import { usePlayerData } from '../../hooks/usePlayerData'

interface PlayerContextValue {
  state: PlayerState
  refetch: () => void
}

const PlayerContext = createContext<PlayerContextValue | null>(null)

interface PlayerProviderProps {
  playerId: string
  children: React.ReactNode
}

export function PlayerProvider({ playerId, children }: PlayerProviderProps) {
  const [state, dispatch] = useReducer(playerReducer, initialState)
  const { fetchPlayer, fetchHighlights } = usePlayerData()

  const loadData = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      const [playerData, highlightsData] = await Promise.all([
        fetchPlayer(playerId),
        fetchHighlights(playerId),
      ])
      dispatch({ type: 'SET_PLAYER', payload: playerData })
      dispatch({ type: 'SET_HIGHLIGHTS', payload: highlightsData })
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load player'
      dispatch({ type: 'SET_ERROR', payload: message })
    }
  }, [playerId, fetchPlayer, fetchHighlights])

  useEffect(() => {
    loadData()
  }, [loadData])

  const value: PlayerContextValue = {
    state,
    refetch: loadData,
  }

  return (
    <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
  )
}

export function usePlayerContext(): PlayerContextValue {
  const context = useContext(PlayerContext)
  if (!context) {
    throw new Error('usePlayerContext must be used within a PlayerProvider')
  }
  return context
}
